import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { ArrowRight, Handshake, PoundSterling, ListChecks } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { AppShell } from "@/components/AppShell";
import { CollapsibleSection } from "@/components/CollapsibleSection";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useSectionOrder } from "@/lib/layout-order";
import { useOrg } from "@/lib/crm/useOrg";
import { useAccounts, useContracts, useOpportunities, useStages } from "@/lib/crm/useCrm";
import { useSalesOwner } from "@/lib/crm/owner-filter";
import { gbp, isLiveContract, isOpen, money, weightedValue } from "@/lib/crm/types";

const SECTION_KEYS = ["revenue", "customers", "tasks"];

const SECTION_LABELS: Record<string, string> = {
  revenue: "Revenue",
  customers: "Recent customers",
  tasks: "Next tasks",
};

const PERIODS = [
  { key: "week", label: "This week", days: 7 },
  { key: "month", label: "This month", days: 30 },
  { key: "quarter", label: "This quarter", days: 91 },
] as const;

type PeriodKey = (typeof PERIODS)[number]["key"];

export const Route = createFileRoute("/_authenticated/overview")({
  head: () => ({
    meta: [
      { title: "Overview — MyPriorities" },
      {
        name: "description",
        content:
          "Your live dashboard: contracted revenue, weighted pipeline, customers and the tasks due next.",
      },
      { property: "og:title", content: "Overview — MyPriorities" },
      {
        property: "og:description",
        content: "Customers, revenue and tasks in one dashboard view.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: OverviewPage,
});

function greeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

function useOpenTasks() {
  return useQuery({
    queryKey: ["overview", "tasks"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tasks")
        .select("id, title, due_date, important, urgent, hours_week, done")
        .eq("done", false)
        .order("due_date", { ascending: true, nullsFirst: false })
        .limit(200);
      if (error) throw error;
      return data ?? [];
    },
  });
}

function useDoneTasks(days: number) {
  const since = new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
  return useQuery({
    queryKey: ["overview", "completions", days],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("task_completions")
        .select("id, completed_on, hours")
        .gte("completed_on", since)
        .limit(2000);
      if (error) throw error;
      return data ?? [];
    },
  });
}

function OverviewPage() {
  const [period, setPeriod] = useState<PeriodKey>("week");
  const days = PERIODS.find((p) => p.key === period)?.days ?? 7;

  const org = useOrg();
  const orgId = org.data?.organisation?.id ?? null;
  const { scope } = useSalesOwner();

  const stages = useStages(orgId);
  const opportunities = useOpportunities(orgId);
  const contracts = useContracts(orgId);
  const accounts = useAccounts(orgId);
  const openTasks = useOpenTasks();
  const doneTasks = useDoneTasks(days);

  const { visibleOrder, hidden, toggleHidden, move, moveTo, reset, isCustom } = useSectionOrder(
    "overview",
    SECTION_KEYS,
  );

  const stageRows = stages.data ?? [];
  const oppRows = scope(opportunities.data ?? []);
  const contractRows = scope(contracts.data ?? []);
  const accountRows = accounts.data ?? [];

  const liveContracts = contractRows.filter(isLiveContract);
  const contractedRevenue = liveContracts.reduce((s, c) => s + money(c.annual_value), 0);
  const openOpps = oppRows.filter((o) => isOpen(o, stageRows));
  const weighted = openOpps.reduce((s, o) => s + weightedValue(o, stageRows), 0);

  const tasks = openTasks.data ?? [];
  const today = new Date().toISOString().slice(0, 10);
  const dueSoon = tasks.filter((t) => t.due_date && t.due_date <= today);
  const completed = doneTasks.data ?? [];
  const hoursLogged = completed.reduce((s, c) => s + money(c.hours as number | null), 0);

  const revenueByAccount = useMemo(() => {
    const totals = new Map<string, number>();
    for (const contract of liveContracts) {
      const name = accountRows.find((a) => a.id === contract.account_id)?.name ?? "Unassigned";
      totals.set(name, (totals.get(name) ?? 0) + money(contract.annual_value));
    }
    return [...totals.entries()]
      .map(([name, value]) => ({ name, value: Math.round(value) }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
  }, [liveContracts, accountRows]);

  const recentAccounts = useMemo(
    () =>
      [...accountRows]
        .sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""))
        .slice(0, 6),
    [accountRows],
  );

  const nextTasks = useMemo(
    () =>
      [...tasks]
        .sort((a, b) => {
          const score = (t: (typeof tasks)[number]) =>
            (t.important ? 0 : 1) + (t.urgent ? 0 : 1);
          const byScore = score(a) - score(b);
          if (byScore !== 0) return byScore;
          return (a.due_date ?? "9999").localeCompare(b.due_date ?? "9999");
        })
        .slice(0, 8),
    [tasks],
  );

  const sectionProps = (key: string) => {
    const index = visibleOrder.indexOf(key);
    return {
      onMove: (direction: -1 | 1) => move(key, direction),
      canMoveUp: index > 0,
      canMoveDown: index >= 0 && index < visibleOrder.length - 1,
      onDragStart: (event: React.DragEvent<HTMLElement>) => {
        event.dataTransfer.setData("text/plain", key);
        event.dataTransfer.effectAllowed = "move";
      },
      onDragOver: (event: React.DragEvent<HTMLElement>) => event.preventDefault(),
      onDrop: (event: React.DragEvent<HTMLElement>) => {
        event.preventDefault();
        const dragged = event.dataTransfer.getData("text/plain");
        if (dragged) moveTo(dragged, key);
      },
    };
  };

  const sections: Record<string, React.ReactNode> = {
    revenue: (
      <CollapsibleSection
        key="revenue"
        storageKey="overview:revenue"
        title="Revenue"
        description="Annualised value of live contracts, by customer."
        summary={<span className="text-sm text-muted-foreground">{gbp(contractedRevenue)}</span>}
        action={
          <Button asChild variant="ghost" size="sm">
            <Link to="/sales">
              Sales <ArrowRight className="size-4" />
            </Link>
          </Button>
        }
        {...sectionProps("revenue")}
      >
        {revenueByAccount.length === 0 ? (
          <EmptyPanel
            text="No live contracts yet. Add a customer and a contract to see revenue here."
            to="/sales/contracts"
            cta="Add a contract"
          />
        ) : (
          <div className="mt-3 h-64 rounded-lg border border-border bg-card p-3">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenueByAccount}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => gbp(v)} width={78} />
                <Tooltip formatter={(v) => gbp(Number(v))} />
                <Bar dataKey="value" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CollapsibleSection>
    ),
    customers: (
      <CollapsibleSection
        key="customers"
        storageKey="overview:customers"
        title="Recent customers"
        description="The accounts added most recently."
        summary={<span className="text-sm text-muted-foreground">{accountRows.length} total</span>}
        action={
          <Button asChild variant="ghost" size="sm">
            <Link to="/sales/accounts">
              All customers <ArrowRight className="size-4" />
            </Link>
          </Button>
        }
        {...sectionProps("customers")}
      >
        {recentAccounts.length === 0 ? (
          <EmptyPanel
            text="No customers yet."
            to="/sales/accounts"
            cta="Add a customer"
          />
        ) : (
          <ul className="mt-3 divide-y divide-border rounded-lg border border-border bg-card">
            {recentAccounts.map((account) => {
              const value = liveContracts
                .filter((c) => c.account_id === account.id)
                .reduce((s, c) => s + money(c.annual_value), 0);
              return (
                <li key={account.id} className="flex items-center justify-between gap-3 px-3 py-2">
                  <Link
                    to="/sales/accounts"
                    className="min-w-0 truncate text-sm hover:underline"
                  >
                    {account.name}
                  </Link>
                  <span className="shrink-0 text-sm text-muted-foreground">{gbp(value)}</span>
                </li>
              );
            })}
          </ul>
        )}
      </CollapsibleSection>
    ),
    tasks: (
      <CollapsibleSection
        key="tasks"
        storageKey="overview:tasks"
        title="Next tasks"
        description="Important and urgent work first, then whatever is due soonest."
        summary={<span className="text-sm text-muted-foreground">{tasks.length} open</span>}
        action={
          <Button asChild variant="ghost" size="sm">
            <Link to="/work">
              Work hub <ArrowRight className="size-4" />
            </Link>
          </Button>
        }
        {...sectionProps("tasks")}
      >
        {nextTasks.length === 0 ? (
          <EmptyPanel text="Nothing open — add a task to get going." to="/work" cta="Open Work hub" />
        ) : (
          <ul className="mt-3 divide-y divide-border rounded-lg border border-border bg-card">
            {nextTasks.map((task) => (
              <li key={task.id} className="flex items-center justify-between gap-3 px-3 py-2">
                <span className="min-w-0 truncate text-sm">{task.title}</span>
                <span className="flex shrink-0 items-center gap-1.5">
                  {task.important ? <Badge variant="secondary">Important</Badge> : null}
                  {task.urgent ? <Badge variant="destructive">Urgent</Badge> : null}
                  {task.due_date ? (
                    <span className="text-xs text-muted-foreground">{task.due_date}</span>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CollapsibleSection>
    ),
  };

  return (
    <AppShell
      title={`${greeting()} — here's your day`}
      subtitle="Customers, revenue and tasks in one view."
      action={
        <div className="flex flex-wrap items-center gap-1">
          {PERIODS.map((p) => (
            <Button
              key={p.key}
              size="sm"
              variant={period === p.key ? "default" : "outline"}
              onClick={() => setPeriod(p.key)}
            >
              {p.label}
            </Button>
          ))}
        </div>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile
          icon={<PoundSterling className="size-4" />}
          label="Contracted revenue"
          value={gbp(contractedRevenue)}
          hint={`${liveContracts.length} live contracts`}
        />
        <Tile
          icon={<Handshake className="size-4" />}
          label="Weighted pipeline"
          value={gbp(weighted)}
          hint={`${openOpps.length} open opportunities`}
        />
        <Tile
          icon={<ListChecks className="size-4" />}
          label="Open tasks"
          value={String(tasks.length)}
          hint={`${dueSoon.length} due today or overdue`}
        />
        <Tile
          icon={<ListChecks className="size-4" />}
          label="Completed"
          value={String(completed.length)}
          hint={`${hoursLogged.toFixed(1)}h logged ${PERIODS.find((p) => p.key === period)?.label.toLowerCase()}`}
        />
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <span className="label-caps">Panels</span>
        {SECTION_KEYS.map((key) => (
          <Button
            key={key}
            size="sm"
            variant={hidden.includes(key) ? "outline" : "secondary"}
            onClick={() => toggleHidden(key)}
          >
            {SECTION_LABELS[key]}
          </Button>
        ))}
        {isCustom ? (
          <Button size="sm" variant="ghost" onClick={reset}>
            Reset layout
          </Button>
        ) : null}
      </div>

      {visibleOrder.map((key) => sections[key])}
    </AppShell>
  );
}

function Tile({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
        {icon} {label}
      </p>
      <p className="mt-1 font-display text-2xl font-semibold">{value}</p>
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function EmptyPanel({ text, to, cta }: { text: string; to: string; cta: string }) {
  return (
    <div className="mt-3 rounded-lg border border-dashed border-border p-6 text-center">
      <p className="text-sm text-muted-foreground">{text}</p>
      <Button asChild size="sm" variant="outline" className="mt-3">
        <Link to={to}>{cta}</Link>
      </Button>
    </div>
  );
}
